import { Component, EventEmitter, Inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
// import { BaseComponent } from '../../../modules/tcm-core.module';
import { Subscription } from 'rxjs/internal/Subscription';
import { MedicalReportDetailsComponent } from '../../../actions/medical-report/medical-report-details/medical-report-details.component';
import { getPermissionInfo } from '../../../helpers/permission.helpers';
import { BroadCastEvents } from '../../../common/broad-cast-events.enum';
import { FittingReport2GetFormDataRequest } from '../../../models/fitting-report-2';
import { DocumentType, LastAttachedDocument, LastAttachmentsDocumentUploadReqest, LastAttachmentsDocumentUploadResponse, LastAttachmentSourceEnum, LastAttachmentsWidgetRequest, LastAttachmentsWidgetViewModel, MedicalReportModel } from '../../../models/last-attachments-widget';
import { MedicalReportDetailsFormRequest } from '../../../models/medical-report/medical-report-details-form-request';
import { TcmBaseComponentContext } from '../../../modules/tcm-common/services/tcm-base-component-context.service';
import { TcmModalService } from '../../../modules/tcm-common/services/tcm-modal.service';
import { PatientDocumentDataService, PermissionsService, DocumentTemplateDataService } from '../../../services';
import { TranslationService } from '../../../services/translation.service';
import { TcmAppSettings } from '../../../common/tcm-app-settings';
import { FittingReportEditorComponent } from '../fitting-report-2-editor/fitting-report-2-editor.component';
import { PatientService } from '../../../services/patient.service';
import { ScreenSourceEnum } from '../../../common/screen-source-enum';
import { RoleAccessSettings, EntityPermissionNode } from '../../../models/roles';
import { Task } from '../../../helpers/task';
import { PatientStatusEnum } from '../../../models/patients/patient-status-enum';
import { SystemPreferences, SYSTEM_PREFERENCES } from '../../../common/system-preferences';
import { BaseComponent } from '../../tcm-core.module/components/base-component/base-component.component';

@Component({
    selector: 'last-attachments-widget',
    templateUrl: './last-attachments-widget.component.html',
    styleUrls: ['./last-attachments-widget.component.scss'],
    standalone: false
})
export class LastAttachmentsWidgetComponent extends BaseComponent<LastAttachmentsWidgetRequest, LastAttachmentsWidgetViewModel> {

  private patientStatusChangedSubscription: Subscription;

  @Input() lastAttachmentSource: LastAttachmentSourceEnum;
  @Input()
  patientStatus: PatientStatusEnum;

  @Output() documentChanged = new EventEmitter<any>();

  lastAttachmentSourceEnum: any = LastAttachmentSourceEnum;

  docTypePairs: Array<Array<DocumentAdapter>>;

  isAccessible: boolean = false;

  currentPermissions: {
    documents: EntityPermissionNode,
    medicalReports: EntityPermissionNode,
    fittingReport: EntityPermissionNode
  } = {} as any;

  private readonly permissionsKeys = {
    documents: getPermissionInfo('patients', 'patientDocuments'),
    medicalReports: getPermissionInfo('patients', 'medicalReports'),
    fittingReport: getPermissionInfo('patients', 'fittingReport')
  };

  @ViewChild('documentGenerationConfirmation', { static: true }) private documentGenerationConfirmationTemplate: TemplateRef<any>;
  
  private refreshSubscription: Subscription;
  private permissionsSubscription: Subscription;

  get patientInactive(): boolean {
    return this.patientStatus == PatientStatusEnum.Inactive || this.patientStatus == PatientStatusEnum.Deceased;
  }

  constructor(protected override componentContext: TcmBaseComponentContext,
    protected override route: ActivatedRoute,
    protected patientDocumentDataService: PatientDocumentDataService,
    private documentTemplateDataService: DocumentTemplateDataService,
    private modalService: TcmModalService,
    private translationService: TranslationService,
    private patientService: PatientService,
    private permissionsSvc: PermissionsService,
    @Inject(SYSTEM_PREFERENCES) private systemPreferences: SystemPreferences
  ) {
    super(componentContext, route);

    this.actionName = 'lastAttachmentsWidget/getWidget';

    this.setWidgetAccessibility(this.permissionsSvc.currentPermissions, true);
    this.patientStatusChangedSubscription = this.componentContext.broadcastService
      .getEvents(BroadCastEvents.PatientStatusChanged)
      .subscribe((e) => this.patientStatus = e.args.status);
  }

  override async ngAfterViewInit() {
    super.ngAfterViewInit();

    await Task.delay(0);

    this.permissionsSubscription = this.permissionsSvc.permissions$.subscribe(x => this.setWidgetAccessibility(x));
    this.refreshSubscription = this.componentContext.broadcastService
      .getEvents(BroadCastEvents.PatientDocumentChanged, BroadCastEvents.MedicalReportChanged)
      .subscribe(() => this.refresh());
  }

  override ngOnDestroy() {
    super.ngOnDestroy();

    this.refreshSubscription.unsubscribe();

    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe();
    }

    this.patientStatusChangedSubscription.unsubscribe();
  }  

  async onFileChange($event: Event, documentType: DocumentType) {
    $event.stopPropagation()

    const request: LastAttachmentsDocumentUploadReqest = {
      patientId: this.requestModel.patientId,
      documentTypeId: documentType.id,
      appointmentId: this.requestModel.appointmentId,
      salesOrderId: this.requestModel.salesOrderId
    };

    const fileInputElement = $event.target as HTMLInputElement;
    const file = fileInputElement.files[0];
    const maxUploadingFileSizeMB = this.systemPreferences.fileUploadSize;
    const fileExtensions = this.systemPreferences.fileExtensions;

    let error = null;
    const allowedFileExtensions = fileExtensions.split(" ");    
    
    if (allowedFileExtensions && allowedFileExtensions.length > 0 && !allowedFileExtensions.some(x => x === '*')) {
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      if (!allowedFileExtensions.some(x => x === fileExtension || (x.toLowerCase() === fileExtension.toLowerCase()))) {
        error = this.translationService.translate(x => x.Messages.ErrorMessages.FileTypeUnsupported).replace('{0}', fileExtension);
      }
    }

    if (error == null && file.size > maxUploadingFileSizeMB * 1024 * 1024) {
      error = this.translationService.translate(x => x.Messages.ErrorMessages.FileSizeLimitReached)
        .replace('{0}', file.name)
        .replace('{1}', maxUploadingFileSizeMB);      
    }

    if (error != null) {
      $event.preventDefault();
      fileInputElement.value = "";

      this.modalService.showErrorModalAsync(x => x.Messages.MessageHeadersAndActions.Error, error);

      return;
    }

    let response: LastAttachmentsDocumentUploadResponse = await this.patientDocumentDataService.uploadDocumentFileAsync(file, request);

    this.componentContext.broadcastService.next({ name: BroadCastEvents.PatientDocumentChanged, args: { referralDate: response.referralDate } });
  }

  async onAttachmentClick($event, doc: LastAttachedDocument) {
    $event.stopPropagation();

    this.patientDocumentDataService.downloadAndSaveDocumentFileAsync(doc.id);
  }

  async openMedicalReport($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.viewModel.medicalReport && !await this.validatePatientInfo()) {
      return;
    }

    const request: MedicalReportDetailsFormRequest = {
      patientId: this.requestModel.patientId,
      appointmentId: this.requestModel.appointmentId,
      salesOrderId: this.requestModel.salesOrderId,
      id: this.viewModel.medicalReport && this.viewModel.medicalReport.id
    }

    this.componentContext.modalService.openSecureComponentInModal(MedicalReportDetailsComponent, {
      permissionsInfo: this.permissionsKeys.medicalReports,
      requestModel: request
    }, "lg");
  }

  openFittingReport(fittingReportId: number) {
    const request: FittingReport2GetFormDataRequest = {
      patientId: this.requestModel.patientId,
      salesOrderId: this.requestModel.salesOrderId,
      fittingReportId: fittingReportId,
      isPatientActive: !this.patientInactive
    }

    this.componentContext.modalService.openSecureComponentInModal(FittingReportEditorComponent,
      {
        permissionsInfo: this.permissionsKeys.fittingReport,
        requestModel: request
      },
      "lg");
  }

  protected override onViewModelChanged() {
    super.onViewModelChanged();

    const isSaleOrderAttachmentSource = this.lastAttachmentSource == LastAttachmentSourceEnum.SaleOrder;

    let requiredMissingSalesOrderDocumentsCount = 0;
    if (isSaleOrderAttachmentSource) {
      requiredMissingSalesOrderDocumentsCount = this.viewModel.documentTypes.filter((x) =>
        x.isRequiredInSaleOrder &&
        !x.lastAttachedDocument).length;
    }

    const docsInfo = {
      requiredMissingSalesOrderDocumentsCount: requiredMissingSalesOrderDocumentsCount
    };

    this.splitDocumentsByChunkAndPermissions();

    this.documentChanged.emit(docsInfo);
  }

  async printAllDocumentsAsync() {
    this.isLoading = true;
    try {
      var fileResponse = await this.patientDocumentDataService.aggregatePatientDocumentsAsync(this.requestModel, this.componentCancellationTokenSource.createDeferred().promise());

      await fileResponse.displayFileAsync();
    } finally {
      this.isLoading = false;
    }
  }

  protected createRequestModel(): LastAttachmentsWidgetRequest {
    return {} as LastAttachmentsWidgetRequest;
  }

  protected override refreshViewModel() {
    if (this.isAccessible) {
      super.refreshViewModel();
    }
  }

  private validatePatientInfo() {

    let screenSource: ScreenSourceEnum = null;

    switch (this.lastAttachmentSource) {
      case LastAttachmentSourceEnum.Appointment:
        screenSource = ScreenSourceEnum.Appointment;
        break;
      case LastAttachmentSourceEnum.Patient:
        screenSource = ScreenSourceEnum.Patient;
        break;
      case LastAttachmentSourceEnum.SaleOrder:
        screenSource = ScreenSourceEnum.SaleOrder;
        break;
    }

    return this.patientService.validatePatientInfoAsync(
      this.requestModel.patientId,
      null,
      screenSource,
      null,
      this.componentCancellationTokenSource.promise()
    );
  }

  private splitDocumentsByChunkAndPermissions() {

    if (!this.viewModel || !this.viewModel.documentTypes || !this.viewModel.documentTypes.length) {
      this.docTypePairs = [];
      return;
    }

    const documents: DocumentAdapter[] = this.viewModel.documentTypes.map(doc => {
      return {
        isPlaceHolder: false,
        isFittingReport: doc.isFittingReport2,
        docType: doc
      };
    });

    documents.splice(0, 0, {
      isPlaceHolder: false,
      isMedicalReport: true,
      medicalReport: this.viewModel.medicalReport
    });

    const chunks: DocumentAdapter[][] = documents.filter(adp => {
      if (adp.isMedicalReport)
        return this.currentPermissions.medicalReports.canRead;

      if (adp.isFittingReport)
        return this.currentPermissions.fittingReport.canRead;

      return this.currentPermissions.documents.canRead;
    }).splitByChunks(2);

    if (chunks.length) {
      const lastChunk = chunks[chunks.length - 1];

      if (lastChunk.length < 2) {
        lastChunk.push({ isPlaceHolder: true });
      }
    }

    this.docTypePairs = chunks;
  }

  private setWidgetAccessibility(role: RoleAccessSettings, init: boolean = false) {

    if (!role.entities.patients.patientDocuments.canRead) {
      this.isAccessible = false;
      return;
    }

    this.currentPermissions.documents = role.entities.patients.patientDocuments;
    this.currentPermissions.medicalReports = role.entities.patients.medicalReports;
    this.currentPermissions.fittingReport = role.entities.patients.fittingReport;

    const isAccessiblePrevious = this.isAccessible;

    this.isAccessible =
      this.currentPermissions.documents.canRead ||
      this.currentPermissions.medicalReports.canRead ||
      this.currentPermissions.fittingReport.canRead;

    if (init)
      return;

    if (this.isAccessible) {
      if (!isAccessiblePrevious) {
        this.refresh();
      }
      else {
        this.splitDocumentsByChunkAndPermissions();
      }
    }
  }

  public async onGeneratePatientForm(documentTypeId: number){
    var result = await this.documentTemplateDataService.generatePatientFormAsync(
      this.requestModel.patientId, this.requestModel.salesOrderId, this.requestModel.appointmentId, documentTypeId, this.componentCancellationTokenSource.promise()
    );

    this.componentContext.broadcastService.next({ name: BroadCastEvents.PatientDocumentChanged });

    const shouldDownload = await this.componentContext.modalService.showSuccessModalAsync<boolean>(
      x => x.Messages.MessageHeadersAndActions.Success,
      this.documentGenerationConfirmationTemplate, 'sm', null, {}
    );

    if (shouldDownload) {
      await this.patientDocumentDataService.downloadAndSaveDocumentFileAsync(result.patientDocumentId);
    }
  }
}


interface DocumentAdapter {
  isPlaceHolder: boolean;
  isMedicalReport?: boolean;
  isFittingReport?: boolean;
  medicalReport?: MedicalReportModel
  docType?: DocumentType
}
